import { createFileRoute, Link } from "@tanstack/react-router"
import { HugeiconsIcon } from "@hugeicons/react"
import ArrowRight02Icon from "@hugeicons/core-free-icons/ArrowRight02Icon"
import Message01Icon from "@hugeicons/core-free-icons/Message01Icon"
import Link04Icon from "@hugeicons/core-free-icons/Link04Icon"
import Mail01Icon from "@hugeicons/core-free-icons/Mail01Icon"
import SmartPhone01Icon from "@hugeicons/core-free-icons/SmartPhone01Icon"
import Heading from "@/components/layout/Heading"
import { Button } from "@/components/ui/button"
import { createLLMOMeta, createBreadcrumbSchema } from "@/lib/seo"
import { useAuth } from "@/components/auth/AuthProvider"
import { useAnalytics } from "@/lib/analytics"
import { getUseCasesForLanguage } from "@/data/use-cases/use-cases-i18n"
import { useTranslation } from "@/lib/i18n"
import CTA from "@/components/LandingPage/CTA"

const iconComponents = {
  MessageSquare: Message01Icon, Link: Link04Icon, Mail: Mail01Icon, Smartphone: SmartPhone01Icon,
} as const

export const Route = createFileRoute("/use-case/")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: "Instagram DM Automation Use Cases | Zapify",
        description:
          "Explore how creators and brands use Zapify to automate Instagram DMs — drive conversions, collect emails, auto-respond to comments, send links, run giveaways, and more.",
        url: "https://zapify.pro/use-case",
      }),
      {
        name: "keywords",
        content:
          "Instagram DM automation use cases, Instagram automation examples, comment to DM, story reply automation, collect emails Instagram, giveaway automation",
      },
    ],
    links: [{ rel: "canonical", href: "https://zapify.pro/use-case" }],
    scripts: [
      createBreadcrumbSchema({
        itemListElement: [
          { position: 1, name: "Home", item: "https://zapify.pro" },
          { position: 2, name: "Use Cases", item: "https://zapify.pro/use-case" },
        ],
      }),
    ],
  }),
  component: UseCaseDirectoryPage,
})

function UseCaseDirectoryPage() {
  const { track } = useAnalytics()
  const { openAuth } = useAuth()
  const { translations: t, language } = useTranslation()
  const localizedUseCases = getUseCasesForLanguage(language)

  const handleCTA = (ctaId: string, buttonText: string) => {
    track("cta_clicked", { cta_id: ctaId, button_text: buttonText })
    openAuth()
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-white dark:bg-black transition-colors duration-300">
      {/* Radial fade background */}
      <div className="absolute inset-0 bg-white/50 dark:bg-black/20 [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />
      {/* Dotted background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle,#80808015_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" />

      <div className="relative z-10 pt-28 sm:pt-36 pb-12">
        {/* Hero */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 text-center mb-16 sm:mb-20">
          <Heading
            title={<>{t.useCase.heading}</>}
            highlight={<span className="gradient-text">{t.useCase.headingHighlight}</span>}
            showLine={false}
            badge={t.nav.useCases}
            description={t.useCase.description}
            className="mb-8 sm:mb-10"
          />

          <Button
            size="lg"
            className="capitalize font-bold px-8 py-5 sm:px-10 sm:py-6 rounded-full bg-primary-gradient text-primary-foreground text-base shadow-md shadow-primary/20 hover:brightness-[0.97] hover:-translate-y-0.5 transition-all duration-300 group"
            onClick={() => handleCTA("CTA_UseCases_Hero", "Start For Free")}
          >
            {t.useCase.startForFree}
            <HugeiconsIcon icon={ArrowRight02Icon} size={20} color="currentColor" className="ml-2" />
          </Button>
        </section>

        {/* Use Cases Grid */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {localizedUseCases.map((uc) => (
              <Link
                key={uc.slug}
                to={`/use-case/${uc.slug}` as string}
                className="group rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 hover:border-neutral-300 dark:hover:border-neutral-700 hover:shadow-lg transition-all duration-300"
              >
                {(() => { const Icon = iconComponents[uc.icon as keyof typeof iconComponents]; return Icon ? <div className={`w-11 h-11 rounded-xl ${uc.color.bg} flex items-center justify-center mb-4`}><HugeiconsIcon icon={Icon} size={20} color="currentColor" className={uc.color.text} /></div> : null })()}
                <h3 className="text-lg font-bold text-neutral-900 dark:text-white mb-2 group-hover:text-brand-button transition-colors">
                  {uc.name}
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4 line-clamp-2">
                  {uc.description}
                </p>
                <span className="inline-flex items-center text-sm font-semibold text-brand-button group-hover:gap-2 transition-all">
                  {t.useCase.learnMore}
                  <HugeiconsIcon icon={ArrowRight02Icon} size={16} color="currentColor" className="ml-1" />
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* Bottom CTA */}
        {/* <section className="relative overflow-hidden py-16 sm:py-24 px-4 sm:px-6">
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-brand-accent/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-2xl mx-auto text-center">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-neutral-900 dark:text-white mb-4 sm:mb-6 tracking-tight">
              {t.useCase.bottomCtaHeading}
            </h2>
            <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400 mb-8 sm:mb-10 max-w-xl mx-auto">
              {t.useCase.bottomCtaDesc}
            </p>

            <Button
              variant="gradient-brand"
              className="uppercase font-semibold px-10 py-6 rounded-2xl text-lg group"
              onClick={() => handleCTA("CTA_UseCases_Bottom", "Start For Free")}
            >
              {t.useCase.startForFree}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>

            <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">
              {t.useCase.freeToStart}
            </p>
          </div>
        </section> */}
              <CTA />
      </div>
    </div>
  )
}
